/**
 * The stretch of days a performance report is about.
 *
 * ## Why this is not `new Date()` arithmetic in a component
 *
 * A period is a pair of **calendar days in the company's timezone**. Every
 * boundary here is computed from the company's today — the `today` that
 * `useFormatters()` supplies, which is the company's calendar day carried into
 * the browser's arithmetic — and never from the browser's own midnight.
 *
 * The distinction is not theoretical. Session 7 found the product deriving
 * follow-up and cheque state from the browser's day while the database used the
 * company's: invisible to anybody sitting in that timezone, and for everybody
 * else a few hours each night where the two disagreed. A month boundary is the
 * same bug with a larger blast radius — "last month" starting a day early puts
 * a whole day of somebody's work in the wrong report.
 *
 * ## Days, not moments
 *
 * A period is expressed as two `YYYY-MM-DD` strings and handed to SQL as dates,
 * which compares them against `(created_at at time zone <company zone>)::date`.
 * Nothing here converts to an instant, because "this month" is not an instant
 * and pretending it is invites exactly one off-by-one-day bug per timezone.
 */

export type ReportPreset = 'today' | 'week' | 'month' | 'previous_month' | 'custom';

export interface ReportPeriod {
    preset: ReportPreset;
    /** Inclusive, `YYYY-MM-DD`, in the company's calendar. */
    from: string;
    /** Inclusive. */
    to: string;
}

export interface PresetDefinition {
    preset: ReportPreset;
    label: string;
    /** What the period covers, for the control's title. */
    hint: string;
}

export const REPORT_PRESETS: readonly PresetDefinition[] = [
    { preset: 'today', label: 'Today', hint: 'What has happened so far today' },
    { preset: 'week', label: 'This week', hint: 'Monday to today' },
    { preset: 'month', label: 'This month', hint: 'The first of the month to today' },
    { preset: 'previous_month', label: 'Last month', hint: 'The whole of the previous calendar month' },
    { preset: 'custom', label: 'Custom range', hint: 'Two dates of your own' },
];

export const isReportPreset = (value: unknown): value is ReportPreset =>
    REPORT_PRESETS.some(p => p.preset === value);

/** `YYYY-MM-DD` from a Date's *local* parts, which is where the company's day lives. */
export const dayValue = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const shifted = (from: Date, days: number): Date => {
    const d = new Date(from);
    d.setDate(d.getDate() + days);
    return d;
};

/**
 * The days a preset covers, counted from the company's today.
 *
 * "This week" runs Monday to today rather than Sunday to today, and rather than
 * Monday to Sunday: a collections week is the days worked so far, and a report
 * that included days that have not happened would show every Monday as a
 * disastrous week.
 */
export function periodFor(
    preset: ReportPreset,
    today: Date,
    custom?: { from?: string; to?: string },
): ReportPeriod {
    const anchor = new Date(today);
    anchor.setHours(0, 0, 0, 0);

    switch (preset) {
        case 'today':
            return { preset, from: dayValue(anchor), to: dayValue(anchor) };

        case 'week': {
            // getDay(): 0 is Sunday. Monday is the start of a working week, so
            // Sunday counts back six days rather than starting a new one.
            const weekday = anchor.getDay();
            const backTo = weekday === 0 ? 6 : weekday - 1;
            return { preset, from: dayValue(shifted(anchor, -backTo)), to: dayValue(anchor) };
        }

        case 'month': {
            const first = new Date(anchor);
            first.setDate(1);
            return { preset, from: dayValue(first), to: dayValue(anchor) };
        }

        case 'previous_month': {
            const firstOfThis = new Date(anchor);
            firstOfThis.setDate(1);
            const lastOfPrevious = shifted(firstOfThis, -1);
            const firstOfPrevious = new Date(lastOfPrevious);
            firstOfPrevious.setDate(1);
            return { preset, from: dayValue(firstOfPrevious), to: dayValue(lastOfPrevious) };
        }

        case 'custom': {
            const valid = (value: string | undefined): string | null =>
                value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : null;
            const from = valid(custom?.from);
            const to = valid(custom?.to);
            // A half-filled custom range falls back to the month rather than
            // asking the database about a range with one end missing.
            if (!from || !to) return periodFor('month', today);
            // Given backwards, read forwards. Somebody picking the end date
            // first should not be told their range is empty.
            return from <= to
                ? { preset, from, to }
                : { preset, from: to, to: from };
        }
    }
}

/** How many days a period covers, inclusive. */
export function periodLength(period: ReportPeriod): number {
    const from = new Date(`${period.from}T00:00:00`);
    const to = new Date(`${period.to}T00:00:00`);
    if (isNaN(from.getTime()) || isNaN(to.getTime())) return 0;
    return Math.round((to.getTime() - from.getTime()) / 86_400_000) + 1;
}

/** The period, read out of the address bar, so a report can be linked to. */
export function periodFromParams(params: URLSearchParams, today: Date): ReportPeriod {
    const preset = params.get('period');
    return periodFor(
        isReportPreset(preset) ? preset : 'month',
        today,
        { from: params.get('from') ?? undefined, to: params.get('to') ?? undefined },
    );
}
